// lib/bruchtestVorschau.js
//
// Was das Bruchtest-Panel an Punkten ANZEIGT, bevor gespeichert wird.
//
// Gerechnet wird eine Bruchtest-Wertung in backend/utils/bruchtestScoring.js —
// dort und nirgends sonst. Diese Datei ist die Vorschau dazu: sie muss
// dieselbe Zahl liefern, sonst zeigt der Schreibtisch etwas anderes an, als
// nachher in der Datenbank steht.
//
// Genau das war der Fall: im Panel standen sechs eigene Abschriften der
// Formel, die sich in drei Punkten unterschieden — und keine davon stimmte mit
// dem Server ueberein.
//
// Damit die beiden nicht wieder auseinanderlaufen, vergleicht
// backend/utils/__tests__/bruchtestVorschau.test.js diese Funktion bei jedem
// `npm test` mit der echten Rechnung.

/** Kein Bruch, keine Wertung — das gilt in jedem Modus. */
const istErfolgreich = (b) => !!b.erfolg;

const runde = (z) => Math.round(z * 100) / 100;

/** Eine Note von 1 bis 10 wird zum Faktor 0,1 bis 1,0. Fehlt sie, gilt 5. */
const note = (wert) => (Number(wert) || 5) / 10;

/**
 * Vorschau auf die Punkte einer einzelnen Technik.
 *
 * @param {object} item    Zeile aus dem Panel: { anzahl, material, mitSprung,
 *                         schwierigkeit, technikWert, ausfuehrung, kraft,
 *                         versuche, zusatzpunkte, erfolg }
 * @param {object} technik Eintrag aus dem Katalog: { multiplikator, kategorie }
 * @param {object} katalog { materialien, sprungMultiplikatoren } — kommt von
 *                         GET /api/bruchtest/techniken
 * @param {string} modus   'points' | 'pass_fail' | 'count'
 * @returns {number} Punkte, gerundet wie der Server
 */
function vorschauPunkte(item, technik, katalog = {}, modus = 'points') {
  const multiplikator = Number(technik?.multiplikator) || 1.0;
  const anzahl = Number(item?.anzahl) || 0;

  if (modus === 'pass_fail') return istErfolgreich(item) ? 1 : 0;
  if (modus === 'count') return istErfolgreich(item) ? anzahl : 0;

  const materialien = katalog.materialien || [];
  const material = materialien.find((m) => m.id === item?.material);
  const basisPunkte = Number(material?.basisPunkte) || 10;

  const sprungTabelle = katalog.sprungMultiplikatoren || {};
  const sprung = item?.mitSprung ? (Number(sprungTabelle[technik?.kategorie]) || 1) : 1;

  const versuche = Number(item?.versuche) || 1;
  const versuchAbzug = Math.max(0, 1 - (versuche - 1) * 0.2);
  const zusatzpunkte = Number(item?.zusatzpunkte) || 0;

  const faktoren = note(item?.schwierigkeit)
    * note(item?.technikWert)
    * note(item?.ausfuehrung)
    * note(item?.kraft);

  if (!istErfolgreich(item)) return 0;

  return runde(anzahl * basisPunkte * multiplikator * sprung * faktoren * versuchAbzug + zusatzpunkte);
}

/** Summe aller Techniken eines Teilnehmers — so wertet der Server die Rangfolge. */
function vorschauSumme(items, technikFuer, katalog = {}, modus = 'points') {
  const liste = Array.isArray(items) ? items : [];
  return runde(liste.reduce(
    (summe, item) => summe + vorschauPunkte(item, technikFuer(item), katalog, modus),
    0
  ));
}

module.exports = { vorschauPunkte, vorschauSumme };
